import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../utils/config";

function toISODate(v) {
  if (!v) return "";
  return String(v).slice(0, 10);
}

function displayOrDash(val) {
  const s = typeof val === "string" ? val.trim() : val;
  return s ? String(s) : "-";
}

function sumAvailableFromItem(item) {
  // Use global available if present
  if (item?.quantity_available !== undefined && item?.quantity_available !== null) {
    const n = Number(item.quantity_available);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function AddItemModal({ open, onClose, onAdd, existingIds }) {
  const token = localStorage.getItem("accessToken");

  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!open) return;

    setQ("");
    setRows([]);
    setErr("");
    setSelected(null);
    setQty(1);

    // load first page
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/items/?status=active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.detail || "Failed to load items");

        const list = Array.isArray(data) ? data : data.results || [];
        setRows(list);
      } catch (e) {
        console.error(e);
        setErr("Failed to load items.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const doSearch = async () => {
    try {
      setErr("");
      setLoading(true);

      const params = new URLSearchParams();
      params.set("status", "active");
      if (q.trim()) params.set("search", q.trim());

      const res = await fetch(`${API_BASE_URL}/api/items/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed to search items");

      const list = Array.isArray(data) ? data : data.results || [];
      setRows(list);
    } catch (e) {
      console.error(e);
      setErr("Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const selectedAvail = selected ? sumAvailableFromItem(selected) : null;
  const qtyNum = Number(qty || 0);
  const qtyBad = !qtyNum || qtyNum <= 0 || (selectedAvail !== null && qtyNum > selectedAvail);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-card-xl max-w-3xl w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-strong">Add Item</div>
          <button className="text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-brand-500/5" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="flex gap-2 mb-3">
          <input
            className="border border-white/50 dark:border-white/15 rounded px-3 py-2 text-sm w-full"
            placeholder="Search by code / name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") doSearch();
            }}
          />
          <button
            className="px-3 py-2 rounded bg-brand-600 text-white hover:bg-brand-700 text-sm font-semibold"
            onClick={doSearch}
            disabled={loading}
          >
            Search
          </button>
        </div>

        {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
        {loading && <div className="text-sm text-muted mb-2">Loading…</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-white/40 dark:border-white/10 rounded">
            <div className="px-3 py-2 text-xs font-semibold text-muted bg-brand-500/5 border-b">
              Items
            </div>

            <div className="max-h-[320px] overflow-auto">
              {rows.map((it) => {
                const disabled = existingIds.has(it.id);
                const avail = sumAvailableFromItem(it);
                const active = selected?.id === it.id;

                return (
                  <button
                    key={it.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => setSelected(it)}
                    className={
                      "w-full text-left px-3 py-2 border-b last:border-b-0 " +
                      (disabled ? "opacity-50 cursor-not-allowed " : "hover:bg-brand-500/5 ") +
                      (active ? "bg-indigo-50 " : "")
                    }
                    title={disabled ? "Already in list" : ""}
                  >
                    <div className="text-sm font-semibold text-strong">
                      {it.code} — {it.name}
                    </div>
                    <div className="text-xs text-muted">
                      Available: {avail === null ? "-" : avail}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border border-white/40 dark:border-white/10 rounded p-3">
            <div className="text-xs font-semibold text-muted mb-2">Selected</div>

            {!selected ? (
              <div className="text-sm text-muted">Pick an item on the left.</div>
            ) : (
              <>
                <div className="text-sm font-semibold text-strong mb-1">
                  {selected.code} — {selected.name}
                </div>
                <div className="text-xs text-muted mb-3">
                  Available: {selectedAvail === null ? "-" : selectedAvail}
                </div>

                <div className="mb-2">
                  <label className="block text-xs font-semibold text-muted mb-1">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    className={"border rounded px-3 py-2 text-sm w-full " + (qtyBad ? "border-red-400" : "border-white/50 dark:border-white/15")}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                  {qtyBad && (
                    <div className="text-[11px] text-red-600 mt-1">
                      {selectedAvail !== null && qtyNum > selectedAvail
                        ? "Exceeds available"
                        : "Quantity must be > 0"}
                    </div>
                  )}
                </div>

                <button
                  className="px-3 py-2 rounded bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
                  disabled={qtyBad}
                  onClick={() => {
                    onAdd({
                      itemId: selected.id,
                      code: selected.code,
                      name: selected.name,
                      available: selectedAvail,
                      quantity: qtyNum,
                    });
                  }}
                >
                  Add
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EditBorrowBatch() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [batch, setBatch] = useState(null);

  // editable header fields
  const [eventName, setEventName] = useState("");
  const [eventPicName, setEventPicName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [borrowDate, setBorrowDate] = useState("");
  const [expectedReturnDate, setExpectedReturnDate] = useState("");

  // editable items
  const [items, setItems] = useState([]); // { itemId, code, name, available, quantity }

  const [modalOpen, setModalOpen] = useState(false);

  const existingIds = useMemo(() => new Set(items.map((x) => x.itemId)), [items]);

  const validationErrors = useMemo(() => {
    const errs = [];

    if (!eventName.trim()) errs.push("Event name is required.");
    if (!eventPicName.trim()) errs.push("PIC event name is required.");
    if (!eventDate) errs.push("Event date is required.");
    if (!borrowDate) errs.push("Tanggal peminjaman is required.");
    if (!expectedReturnDate) errs.push("Expected return date is required.");

    if (borrowDate && expectedReturnDate && expectedReturnDate < borrowDate) {
      errs.push("Expected return date cannot be earlier than Tanggal peminjaman.");
    }

    if (!items.length) errs.push("Cart cannot be empty.");

    for (const x of items) {
      const qty = Number(x.quantity);
      const avail = x.available === null || x.available === undefined ? null : Number(x.available);

      if (!qty || qty <= 0) {
        errs.push(`${x.code || `Item #${x.itemId}`}: quantity must be > 0`);
        continue;
      }
      if (avail !== null && !Number.isNaN(avail) && qty > avail) {
        errs.push(`${x.code || `Item #${x.itemId}`}: quantity exceeds available (${avail})`);
      }
    }

    return errs;
  }, [eventName, eventPicName, eventDate, borrowDate, expectedReturnDate, items]);

  const canSave = !saving && !loading && validationErrors.length === 0;

  // Load batch detail, then fetch item details for code/available
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${API_BASE_URL}/api/borrow-batches/${batchId}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.detail || "Failed to load batch.");

        setBatch(data);

        const s = String(data.status || "").toUpperCase();
        if (s && s !== "PENDING") {
          throw new Error("Batch is no longer pending.");
        }

        setEventName(displayOrDash(data.event_name) === "-" ? "" : String(data.event_name));
        setEventPicName(displayOrDash(data.event_pic_name) === "-" ? "" : String(data.event_pic_name));
        setEventDate(toISODate(data.event_date));
        setBorrowDate(toISODate(data.borrow_date));
        setExpectedReturnDate(toISODate(data.expected_return_date));

        // For each batch item, fetch item detail to get code + quantity_available
        const baseItems = Array.isArray(data.items) ? data.items : [];
        const built = [];

        for (const bi of baseItems) {
          const itemId = bi.item;
          const qty = Number(bi.quantity);

          let code = "";
          let name = bi.item_name || `Item #${itemId}`;
          let available = null;

          try {
            const ir = await fetch(`${API_BASE_URL}/api/items/${itemId}/`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const idata = await ir.json().catch(() => ({}));
            if (ir.ok) {
              code = idata.code || "";
              name = idata.name || name;
              available = sumAvailableFromItem(idata);
            }
          } catch (e) {
            // ignore, fallback to name only
          }

          built.push({
            itemId,
            code,
            name,
            available,
            quantity: qty,
          });
        }

        setItems(built);
      } catch (e) {
        console.error(e);
        setError(String(e.message || "Failed to load batch."));
      } finally {
        setLoading(false);
      }
    })();
  }, [batchId]);

  const setQty = (itemId, newQty) => {
    setItems((prev) =>
      prev.map((x) => (x.itemId === itemId ? { ...x, quantity: newQty } : x))
    );
  };

  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((x) => x.itemId !== itemId));
  };

  const addItem = (row) => {
    setItems((prev) => {
      if (prev.some((x) => x.itemId === row.itemId)) return prev; // already exists, do nothing
      return [...prev, row];
    });
    setModalOpen(false);
  };
  
  const save = async () => {
    setError("");

    if (!canSave) {
      setError(validationErrors[0] || "Fix validation errors.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        event_name: eventName.trim(),
        event_pic_name: eventPicName.trim(),
        event_date: eventDate,

        borrow_date: borrowDate,
        expected_return_date: expectedReturnDate,

        items: items.map((x) => ({ item_id: x.itemId, quantity: Number(x.quantity) })),
      };

      const res = await fetch(`${API_BASE_URL}/api/admin/borrow-batches/${batchId}/edit/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Handle common backend errors
        if (data?.detail) throw new Error(data.detail);
        if (typeof data === "string") throw new Error(data);
        if (data?.stock?.detail) {
          throw new Error(data.stock.detail);
        }
        throw new Error("Failed to save changes.");
      }

      // Saved: go back to pending list
      navigate("/admin/borrow-batches/pending");
    } catch (e) {
      console.error(e);
      setError(String(e.message || "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-muted">Loading…</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-2xl font-semibold">Edit Borrow Batch</h1>
          <div className="text-xs text-muted">
            Ref: <span className="font-mono">{batch?.reference_code || "-"}</span> • Borrower:{" "}
            {batch?.user || "-"} • Status: {batch?.status || "-"}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded border border-white/50 dark:border-white/15 text-sm font-semibold hover:bg-brand-500/5"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="px-3 py-2 rounded bg-brand-600 text-white hover:bg-brand-700 text-sm font-semibold disabled:opacity-50"
            onClick={save}
            disabled={!canSave}
            title={validationErrors.length ? validationErrors[0] : ""}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

      <div className="glass-card p-4 mb-4">
        <div className="text-sm font-semibold text-strong mb-3">Header</div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Event Name</label>
            <input
              className="border border-white/50 dark:border-white/15 rounded px-3 py-2 text-sm w-full"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">PIC Event Name</label>
            <input
              className="border border-white/50 dark:border-white/15 rounded px-3 py-2 text-sm w-full"
              value={eventPicName}
              onChange={(e) => setEventPicName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Event Date</label>
            <input
              type="date"
              className="border border-white/50 dark:border-white/15 rounded px-3 py-2 text-sm w-full"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>

          {/* Row 2: Dates */}
          <div className="md:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Tanggal Peminjaman
                </label>
                <input
                  type="date"
                  className="border border-white/50 dark:border-white/15 rounded px-3 py-2 text-sm w-full"
                  value={borrowDate}
                  onChange={(e) => setBorrowDate(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted mb-1">
                  Expected return date
                </label>
                <input
                  type="date"
                  className={
                    "border rounded px-3 py-2 text-sm w-full " +
                    (borrowDate && expectedReturnDate && expectedReturnDate < borrowDate
                      ? "border-red-400"
                      : "border-white/50 dark:border-white/15")
                  }
                  value={expectedReturnDate}
                  onChange={(e) => setExpectedReturnDate(e.target.value)}
                />
                {borrowDate && expectedReturnDate && expectedReturnDate < borrowDate && (
                  <div className="text-[11px] text-red-600 mt-1">
                    Expected return date cannot be earlier than Tanggal peminjaman.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-table-wrap overflow-x-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="text-sm font-semibold text-strong">Items</div>
          <button
            className="px-3 py-2 rounded bg-brand-600 text-white hover:bg-brand-700 text-sm font-semibold"
            onClick={() => setModalOpen(true)}
          >
            Add Item
          </button>
        </div>

        <table className="glass-table">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Code</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Name</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Available</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-muted">Qty</th>
              <th className="px-4 py-2 text-right text-xs font-semibold text-muted">Action</th>
            </tr>
          </thead>

          <tbody>
            {items.map((x) => {
              const avail = x.available === null || x.available === undefined ? null : Number(x.available);
              const qty = Number(x.quantity || 0);
              const exceeds = avail !== null && !Number.isNaN(avail) && qty > avail;

              return (
                <tr key={x.itemId} className="border-t">
                  <td className="px-4 py-2 text-xs font-mono text-strong">{x.code || "-"}</td>
                  <td className="px-4 py-2 text-xs text-strong">{x.name}</td>
                  <td className="px-4 py-2 text-xs text-strong">{avail === null ? "-" : avail}</td>

                  <td className="px-4 py-2 text-xs">
                    <input
                      type="number"
                      min="1"
                      className={"border rounded px-2 py-1 text-sm w-28 " + (exceeds ? "border-red-400" : "border-white/50 dark:border-white/15")}
                      value={x.quantity}
                      onChange={(e) => setQty(x.itemId, e.target.value)}
                    />
                    {exceeds && <div className="text-[11px] text-red-600 mt-1">Exceeds available</div>}
                  </td>

                  <td className="px-4 py-2 text-right">
                    <button
                      className="px-3 py-1 rounded bg-red-600 text-white text-xs font-semibold"
                      onClick={() => removeItem(x.itemId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}

            {!items.length && (
              <tr className="border-t">
                <td colSpan={5} className="px-4 py-6 text-sm text-muted">
                  No items in this batch.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddItemModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={addItem}
        existingIds={existingIds}
      />
    </div>
  );
}